import { useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  registerReadState,
  useEquipmentCatalogStore,
  useManufacturerCatalogStore,
} from '@repo/front-end-state';
import { buildPlanSearchIndex, joinRegisterOntoPlan } from '@repo/front-end-utils';
import type {
  PlanSearchCatalogueRow,
  PlanSearchEntry,
  PlanSearchIndexOptions,
  PlanSearchRegisterRow,
} from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';
import Config from '../../common/Config';
import type { PlanSearchTypeStatus } from './PlanEntitySearch';

/**
 * What the title-bar search reads besides the plan (`onboarding-polish-search`), shared by the
 * Floor plan step (`OnboardingPage`) and the Observers step (`OnboardingObservers`) so both search
 * a machine by the same facts:
 *
 * - **Register rows by id** — a placed machine's `assetUid` names one — for its manufacturer,
 *   product line, product and category. Already loaded by the wizard; no read of its own.
 * - **The catalogue by reference id** — for its equipment type. The shared once-per-session
 *   catalogue the planner Library also reads, loaded by `loadTypes` on the search box's first focus.
 *
 * `typeStatus` is the catalogue as the SEARCH sees it. An empty read counts as `error`: the
 * catalogue is never legitimately empty, and "no machine has a type" would be a false claim
 * (spec, Failure States). The Library keeps its own reading of empty.
 */
export function usePlanSearchSources(): {
  /** The register rows themselves, for a caller that must join them onto its plan first. */
  registerRows: ReturnType<typeof useEquipmentCatalogStore.getState>['registerRows'];
  registerByAssetUid: ReadonlyMap<string, PlanSearchRegisterRow>;
  catalogueByReference: ReadonlyMap<string, PlanSearchCatalogueRow> | undefined;
  typeStatus: PlanSearchTypeStatus;
  loadTypes: () => void;
} {
  const { getAccessTokenSilently } = useAuth0();
  const registerRows = useEquipmentCatalogStore(s => s.registerRows);
  const status = useManufacturerCatalogStore(s => s.status);
  const rows = useManufacturerCatalogStore(s => s.rows);
  const byReference = useManufacturerCatalogStore(s => s.byReference);
  const load = useManufacturerCatalogStore(s => s.load);

  const registerByAssetUid = useMemo(
    () => new Map(registerRows.map(row => [String(row.id), row] as const)),
    [registerRows],
  );

  const typeStatus: PlanSearchTypeStatus =
    status === 'ready' && rows.length === 0 ? 'error' : status;

  const loadTypes = useCallback(() => {
    void load(Config.middlewareApi, () => getAccessTokenSilently());
  }, [load, getAccessTokenSilently]);

  return {
    registerRows,
    registerByAssetUid,
    catalogueByReference: typeStatus === 'ready' ? byReference : undefined,
    typeStatus,
    loadTypes,
  };
}

/**
 * A step's search index over its OWN plan, with the register joined onto it
 * (`composites-page-search`, spec FR2) — moved out of the Observers step so the Composite measures
 * step builds the same index the same way rather than a third copy of it.
 *
 * The register is JOINED onto the plan, as the Floor plan searches it: a stored plan's machines
 * often carry no `assetUid` (every one on staging Finsbury Park), and the join is what links a
 * machine to its register row — its manufacturer and, through it, its catalogue type. A stale link
 * is repaired only on a register read that succeeded — the gate the page's own join uses — so every
 * step finds the same machine by the same words (found by review).
 *
 * ⚠ `include` and `extra` are memo dependencies: pass a module constant or a memoised array, or the
 * index (which walks every floor) is rebuilt on every render.
 */
export function useJoinedPlanSearchIndex(
  layout: FloorPlannerLayout | null | undefined,
  {
    twinBid,
    twinId,
    include,
    extra,
  }: {
    twinBid: string | null | undefined;
    twinId: number | null | undefined;
    include?: PlanSearchIndexOptions['include'];
    extra?: PlanSearchIndexOptions['extra'];
  },
): {
  index: PlanSearchEntry[];
  typeStatus: PlanSearchTypeStatus;
  loadTypes: () => void;
} {
  const planSearch = usePlanSearchSources();
  const registerRead = useEquipmentCatalogStore(s =>
    registerReadState(s, { twinId: twinId ?? undefined }),
  );
  const { registerRows, registerByAssetUid, catalogueByReference } = planSearch;
  const index = useMemo(() => {
    const joined = joinRegisterOntoPlan(layout, registerRows, twinBid, {
      repairStaleLinks: registerRead === 'ready',
    });
    return buildPlanSearchIndex(joined, {
      registerByAssetUid,
      catalogueByReference,
      include,
      extra,
    });
  }, [
    layout,
    twinBid,
    registerRead,
    registerRows,
    registerByAssetUid,
    catalogueByReference,
    include,
    extra,
  ]);
  return { index, typeStatus: planSearch.typeStatus, loadTypes: planSearch.loadTypes };
}
